import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Icon, Text } from '../../../../components';
import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { GenerateButton } from '../../../creatives/components/GenerateButton';
import { LumioMark } from '../../../creatives/components/LumioMark';
import { Body, Intro, Lead, Group, GroupTitle, List, Item, Kept, Facts } from './RegenerateSheet.styles';

export type RegenerateSheetProps = {
  /** The style's name, as owners read it. */
  styleName: string;
  /** Website art can be regenerated here (until then the sheet explains and says it is coming soon). */
  available: boolean;
  /** Something else is running (the confirm waits for it). */
  busy: boolean;
  /**
   * Whether the brand's site already shows its own art style: then the new art replaces what players see, in place,
   * as it is drawn (`true`); the site switches to it once all of it is drawn (`false`); or the page cannot tell
   * (`undefined`, after a failed job), and the sheet says both.
   */
  inPlace: boolean | undefined;
  /** "Set as primary" for a style the site does not show: the style is put in use, the whole site is drawn in it,
   *  then the site switches to it. Without it, the sheet is "Regenerate website art" for the style the site shows. */
  adopt?: boolean;
  /** Credits the redraw costs, charged once when it starts: listed with the facts and shown on the confirm (undefined
   *  until the price table loads: neither shows a price rather than guess). */
  cost: number | undefined;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * "Regenerate website art": what redrawing the website's art in the brand's style does, before the owner commits to
 * it. It costs credits, charged once when it starts (listed, and on the confirm, once the price is known), runs for hours and
 * cannot be stopped once started. When the site already shows the brand's style it says plainly that the live art is
 * replaced in place.
 */
export const RegenerateSheet = ({
  styleName,
  available,
  busy,
  inPlace,
  adopt = false,
  cost,
  onConfirm,
  onClose,
}: RegenerateSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const canRun = available && !busy;
  const confirmLabel = adopt ? t('Draw and switch') : t('Regenerate');

  return (
    <Sheet
      title={adopt ? t('Set as primary') : t('Regenerate website art')}
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="520px"
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={dismiss} />
          <GenerateButton
            compact
            label={available ? confirmLabel : t('Coming soon')}
            cost={available ? cost : undefined}
            disabled={!canRun}
            onClick={() => {
              if (!canRun) return;
              onConfirm();
              dismiss();
            }}
          />
        </>
      }
    >
      <Body>
        <Intro>
          <LumioMark size={28} />
          {adopt ? (
            <Lead
              text="Lumio draws the art across your website in {{style}}, then switches your site to it."
              placeholders={{ style: styleName }}
              tag="span"
            />
          ) : (
            <Lead
              text="Lumio redraws the art across your website in {{style}}."
              placeholders={{ style: styleName }}
              tag="span"
            />
          )}
        </Intro>
        {inPlace === true && adopt ? (
          <Callout
            tone="warning"
            title={t('This replaces your live art')}
            text="Your site shows your own look, so players see the new art as each part is drawn. The art they see now is not kept."
          />
        ) : inPlace === true ? (
          <Callout
            tone="warning"
            title={t('This replaces your live art')}
            text="Your site already shows this style, so players see the new art as each part is drawn. The art they see now is not kept. Your original look stays available."
          />
        ) : null}
        <Group>
          <GroupTitle text="Redrawn" tag="span" />
          <List>
            <Item>
              <Icon name="check" size={14} />
              <Text text="Your welcome bonus, hero and promotion banners" tag="span" />
            </Item>
            <Item>
              <Icon name="check" size={14} />
              <Text text="Your gamification art, with its prize, challenge and shop icons" tag="span" />
            </Item>
            <Item>
              <Icon name="check" size={14} />
              <Text text="Your style's characters, wherever they fit" tag="span" />
            </Item>
          </List>
        </Group>
        <Group>
          <GroupTitle text="Kept as it is" tag="span" />
          <List>
            <Kept>
              <Icon name="minus" size={14} />
              <Text text="Your logo" tag="span" />
            </Kept>
            <Kept>
              <Icon name="minus" size={14} />
              <Text text="Game thumbnails" tag="span" />
            </Kept>
            <Kept>
              <Icon name="minus" size={14} />
              <Text text="Calendar and reward prize images" tag="span" />
            </Kept>
          </List>
        </Group>
        <Facts>
          {adopt ? (
            <Item>
              <Icon name="palette" size={14} />
              <Text text="New banners and images follow this style from now on." tag="span" />
            </Item>
          ) : null}
          {cost != null ? (
            <Item>
              <Icon name="wallet" size={14} />
              <Text text={cost === 1 ? '{{n}} credit' : '{{n}} credits'} placeholders={{ n: cost }} tag="span" />
            </Item>
          ) : null}
          <Item>
            <Icon name="clock" size={14} />
            <Text
              text="It can take a few hours and runs to the end once started. You can leave this page."
              tag="span"
            />
          </Item>
          {inPlace === false ? (
            <Item>
              <Icon name="eye" size={14} />
              <Text
                text="Your site switches to the new art once all of it is drawn. Until then, players see your current look."
                tag="span"
              />
            </Item>
          ) : null}
        </Facts>
        {inPlace === undefined ? (
          <Callout
            tone="info"
            text="If your site already shows this style, players see the new art as each part is drawn. Otherwise your site switches to it once all of it is drawn."
          />
        ) : null}
        {available ? null : (
          <Callout tone="info" title="Coming soon" text="We're finishing this. You'll be able to run it from here." />
        )}
      </Body>
    </Sheet>
  );
};

export default RegenerateSheet;
